import { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  updateCallCardFieldSource,
  updateCallCardFieldTitle,
  updateCallCardFieldVisibility
} from '../../api';
import { useCallCardFields } from '../../contexts/CallCardFieldsContext';
import { useToast } from '../../hooks/use-toast';
import { CALL_CARD_FIELD_ORDER, CALL_CARD_FIELD_SLOTS } from '../calls/callCardFields';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue
} from '../ui/select';
import LabelField from './LabelField';
import {
  DEFAULT_SOURCE_VALUE,
  defaultSourceLabel,
  defaultTitle,
  describeSource,
  groupCandidates,
  orphanSource,
  sourceFromSelectValue,
  sourceSelectValue,
  titleDraft
} from './callCardFieldsSettings';
import { normalizeLabelInput } from './nodeChartLabels';

const describeError = (error) =>
  error?.response?.data?.message || error?.message || 'Неизвестная ошибка';

/** Где показывается поле — подпись к галке видимости. */
const VISIBILITY_HINTS = {
  [CALL_CARD_FIELD_SLOTS.SERVICE]: 'Показывать в карточке звонка',
  [CALL_CARD_FIELD_SLOTS.OUTCOME]: 'Показывать в карточке звонка и колонкой журнала'
};

/**
 * «Услуга» и «Итог звонка»: из какого выхода пакета брать значение, как назвать поле и
 * показывать ли его.
 *
 * Источник — любой выход любого узла: у одних пакетов итог лежит в triage, у других — в
 * воронке, а услуги в контракте узлов нет вовсе. Настройка действует на карточку звонка, журнал
 * «Детали звонков» и Excel-выгрузку звонков; видимость Excel не трогает.
 *
 * Состояние берётся из контекста, а ответ каждого PUT уходит в `refresh`: так журнал на соседней
 * вкладке меняет заголовок колонки сразу, без перезагрузки.
 */
const CallCardFieldsCard = () => {
  const { toast } = useToast();
  const { fields, candidates, refresh } = useCallCardFields();

  const [drafts, setDrafts] = useState({});
  const [pending, setPending] = useState({});

  // Черновики названий следуют за сохранённым: после ответа сервера и после refresh().
  const serviceTitle = fields[CALL_CARD_FIELD_SLOTS.SERVICE]?.title;
  const outcomeTitle = fields[CALL_CARD_FIELD_SLOTS.OUTCOME]?.title;
  useEffect(() => {
    setDrafts({
      [CALL_CARD_FIELD_SLOTS.SERVICE]: serviceTitle ?? '',
      [CALL_CARD_FIELD_SLOTS.OUTCOME]: outcomeTitle ?? ''
    });
  }, [serviceTitle, outcomeTitle]);

  const groups = groupCandidates(candidates);
  const hasCandidates = groups.length > 0;

  const save = async (slot, aspect, request, successTitle) => {
    const key = `${slot}:${aspect}`;
    setPending((current) => ({ ...current, [key]: true }));
    try {
      await refresh(await request());
      toast({ title: successTitle });
    } catch (error) {
      // Откат — к тому, что сейчас в контексте: он не менялся, раз запись не прошла.
      setDrafts((current) => ({ ...current, [slot]: titleDraft(fields[slot]) }));
      toast({ title: 'Не удалось сохранить поле карточки', description: describeError(error), variant: 'destructive' });
    } finally {
      setPending((current) => ({ ...current, [key]: false }));
    }
  };

  const saveTitle = (slot, value) => {
    const next = normalizeLabelInput(value);
    if (next === (fields[slot]?.title ?? null)) {
      setDrafts((current) => ({ ...current, [slot]: next ?? '' }));
      return;
    }

    save(slot, 'title', () => updateCallCardFieldTitle(slot, next), 'Название поля сохранено');
  };

  const renderRow = (slot) => {
    const field = fields[slot];
    const orphan = orphanSource(field);
    const selectValue = sourceSelectValue(field);
    const selected = sourceFromSelectValue(selectValue);

    return (
      <div key={slot} data-call-card-field={slot} className="space-y-3 rounded-md border p-3">
        <div className="text-sm font-medium">{defaultTitle(slot)}</div>

        <div className="grid gap-3 md:grid-cols-2">
          <div className="min-w-0 space-y-1">
            <Label htmlFor={`call-card-field-title-${slot}`} className="text-xs text-muted-foreground">
              Название
            </Label>
            <LabelField
              id={`call-card-field-title-${slot}`}
              trackId="settings.call_card_fields.title_edit"
              value={drafts[slot] ?? ''}
              placeholder={defaultTitle(slot)}
              pending={pending[`${slot}:title`] === true}
              renamed={Boolean(field?.title)}
              ariaLabel={`Название поля «${defaultTitle(slot)}»`}
              onChange={(value) => setDrafts((current) => ({ ...current, [slot]: value }))}
              onCommit={() => saveTitle(slot, drafts[slot])}
              onRevert={() => setDrafts((current) => ({ ...current, [slot]: titleDraft(field) }))}
              onReset={() => saveTitle(slot, '')}
            />
          </div>

          <div className="min-w-0 space-y-1">
            <Label htmlFor={`call-card-field-source-${slot}`} className="text-xs text-muted-foreground">
              Источник значения
            </Label>
            <Select
              trackId="settings.call_card_fields.source"
              value={selectValue}
              disabled={!hasCandidates || pending[`${slot}:source`] === true}
              onValueChange={(value) =>
                save(
                  slot,
                  'source',
                  () => updateCallCardFieldSource(slot, sourceFromSelectValue(value)),
                  'Источник поля сохранён'
                )
              }
            >
              <SelectTrigger
                id={`call-card-field-source-${slot}`}
                aria-label={`Источник поля «${defaultTitle(slot)}»`}
                className="h-8"
              >
                <SelectValue>
                  {selected ? describeSource(candidates, selected) : defaultSourceLabel(field, candidates)}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={DEFAULT_SOURCE_VALUE}>{defaultSourceLabel(field, candidates)}</SelectItem>
                {orphan && <SelectItem value={orphan}>{`${orphan} (нет в пакете)`}</SelectItem>}
                {groups.map((group) => (
                  <SelectGroup key={group.nodeId}>
                    <SelectLabel>{group.nodeTitle}</SelectLabel>
                    {group.options.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {orphan && (
          <p className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-400">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              Выхода «{orphan}» нет в активном пакете узлов — поле пустое. Выберите другой источник.
            </span>
          </p>
        )}

        <div className="flex items-center gap-2">
          <Checkbox
            id={`call-card-field-visible-${slot}`}
            data-ev="settings.call_card_fields.visibility_toggle"
            checked={field?.visible !== false}
            disabled={pending[`${slot}:visibility`] === true}
            onCheckedChange={(checked) =>
              save(
                slot,
                'visibility',
                () => updateCallCardFieldVisibility(slot, checked === true),
                checked === true ? 'Поле показано' : 'Поле скрыто'
              )
            }
          />
          <Label htmlFor={`call-card-field-visible-${slot}`} className="text-sm font-normal">
            {VISIBILITY_HINTS[slot]}
          </Label>
        </div>
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Поля карточки звонка</CardTitle>
        <CardDescription>
          «Услуга» и «Итог звонка» в карточке звонка, журнале «Детали звонков» и Excel-выгрузке:
          из какого выхода брать значение, как назвать поле и показывать ли его. Результаты анализа
          не пересчитываются — меняется только показ.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {!hasCandidates && (
          <p className="text-sm text-muted-foreground">
            Источник выбирается из выходов активного пакета узлов. Название и показ поля можно
            настроить и без него.
          </p>
        )}
        {CALL_CARD_FIELD_ORDER.map(renderRow)}
      </CardContent>
    </Card>
  );
};

export default CallCardFieldsCard;
